import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import * as React from "react";

import {
  ApiError,
  requestMeetingReward,
  type DealListItem,
  type MeetingRewardAttendee,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { DateRowInput } from "@/components/ui/date-row-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormError } from "@/components/ui/form-error";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TimeRowInput } from "@/components/ui/time-row-input";
import { dealToasts, useToast } from "@/components/ui/toast";

const INPUT_CLASS =
  "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

interface AttendeeRow {
  name: string;
  title: string;
}

/** Today as YYYY-MM-DD (local) — the meeting already happened, so today is
 * the natural default (mirrors LogDealProgressModal). */
function todayValue(): string {
  const now = new Date();
  const off = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - off).toISOString().slice(0, 10);
}

/** Current time as HH:MM (local) — same auto-populate as LogDealProgressModal. */
function nowTimeValue(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

interface Props {
  deal: DealListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Claim first meeting reward" modal (CHP-464, finishing CHP-387): the
 * direct-payout BDM card's [Claim reward] action. Captures the meeting the
 * claim is for — date, time, attendees, discussion points, next steps — and
 * POSTs /deals/{id}/meeting-reward (flat-$100 claim, CM/RA/RM adjudicated).
 * Field layout mirrors LogDealProgressModal's meeting branch; attendee title
 * is optional HERE because the claim schema keeps it optional (unlike the
 * progress log, where CHP-409 made it mandatory).
 */
export function MeetingRewardClaimModal({ deal, open, onOpenChange }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [date, setDate] = React.useState<string>(() => todayValue());
  const [time, setTime] = React.useState<string>(() => nowTimeValue());
  const [attendees, setAttendees] = React.useState<AttendeeRow[]>([{ name: "", title: "" }]);
  const [keyDiscussionPoints, setKeyDiscussionPoints] = React.useState("");
  const [nextSteps, setNextSteps] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const claim = useMutation({
    mutationFn: () => {
      const cleaned: MeetingRewardAttendee[] = attendees
        .map((a) => ({ name: a.name.trim(), title: a.title.trim() || null }))
        .filter((a) => a.name.length > 0);
      return requestMeetingReward(accessToken!, deal.id, {
        meeting_date: date,
        meeting_time: time,
        attendees: cleaned,
        key_discussion_points: keyDiscussionPoints.trim(),
        next_steps: nextSteps.trim(),
      });
    },
    onSuccess: () => {
      onOpenChange(false);
      toast(dealToasts.meetingRewardClaimed());
      // The card derives from the "me" list's meeting_reward_status — a
      // pending claim suppresses the reminder on the next refetch.
      void queryClient.invalidateQueries({ queryKey: ["deals", "me"] });
      void queryClient.invalidateQueries({ queryKey: ["deals", "meeting-rewards", deal.id] });
    },
    onError: (e) => {
      setError(
        e instanceof ApiError && e.status === 409
          ? "This deal is no longer live, so a meeting reward can't be claimed."
          : "Couldn't submit the claim. Refresh and try again.",
      );
    },
  });

  const validAttendees = attendees.filter((a) => a.name.trim().length > 0);
  const submitDisabled =
    !date ||
    !time ||
    validAttendees.length === 0 ||
    !keyDiscussionPoints.trim() ||
    !nextSteps.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Claim first meeting reward</DialogTitle>
          <DialogDescription>
            Tell us about the qualifying meeting for{" "}
            <span className="font-medium">{deal.client_name}</span>. Your claim is reviewed by the
            ResetData team before the reward is paid.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="reward-meeting-date">Meeting date *</Label>
              <DateRowInput
                id="reward-meeting-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="reward-meeting-time">Meeting time *</Label>
              <TimeRowInput
                id="reward-meeting-time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold">Attendees</legend>
            <div className="flex items-end gap-2">
              <span className="flex-1 text-xs font-medium text-muted-foreground">Name *</span>
              <span className="flex-1 text-xs font-medium text-muted-foreground">
                Title (optional)
              </span>
              <span className="w-9" aria-hidden />
            </div>
            <div className="space-y-2">
              {attendees.map((row, idx) => (
                <div key={idx} className="flex items-end gap-2">
                  <input
                    type="text"
                    aria-label={`Attendee ${idx + 1} name`}
                    value={row.name}
                    onChange={(e) =>
                      setAttendees((rows) =>
                        rows.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)),
                      )
                    }
                    className={`flex-1 ${INPUT_CLASS}`}
                  />
                  <input
                    type="text"
                    aria-label={`Attendee ${idx + 1} title`}
                    value={row.title}
                    onChange={(e) =>
                      setAttendees((rows) =>
                        rows.map((r, i) => (i === idx ? { ...r, title: e.target.value } : r)),
                      )
                    }
                    className={`flex-1 ${INPUT_CLASS}`}
                  />
                  {attendees.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 text-muted-foreground"
                      aria-label={`Remove attendee ${idx + 1}`}
                      onClick={() => setAttendees((rows) => rows.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  ) : (
                    <span className="w-9" aria-hidden />
                  )}
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="reward-add-attendee"
              onClick={() => setAttendees((rows) => [...rows, { name: "", title: "" }])}
            >
              + Add attendee
            </Button>
          </fieldset>

          <div className="space-y-1">
            <Label htmlFor="reward-discussion-points">Key discussion points *</Label>
            <Textarea
              id="reward-discussion-points"
              value={keyDiscussionPoints}
              onChange={(e) => setKeyDiscussionPoints(e.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="Briefly outline what was discussed."
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="reward-next-steps">Next steps *</Label>
            <Textarea
              id="reward-next-steps"
              value={nextSteps}
              onChange={(e) => setNextSteps(e.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="What is the follow-up action?"
            />
          </div>

          {error ? <FormError>{error}</FormError> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="reward-claim-submit"
            disabled={submitDisabled || claim.isPending}
            onClick={() => {
              setError(null);
              claim.mutate();
            }}
          >
            Submit claim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
